import { useState, useEffect, useRef, useCallback } from 'react'
import { reportsAPI } from '../services/api'

const FILE_ICONS = {
  pdf: 'bi-file-earmark-pdf-fill text-red-500',
  doc: 'bi-file-earmark-word-fill text-blue-500',
  docx: 'bi-file-earmark-word-fill text-blue-500',
  xls: 'bi-file-earmark-excel-fill text-green-600',
  xlsx: 'bi-file-earmark-excel-fill text-green-600',
  ppt: 'bi-file-earmark-ppt-fill text-orange-500',
  pptx: 'bi-file-earmark-ppt-fill text-orange-500',
  zip: 'bi-file-earmark-zip-fill text-yellow-600',
  csv: 'bi-file-earmark-spreadsheet-fill text-green-600',
  txt: 'bi-file-earmark-text-fill text-gray-500',
}
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'])
const ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip'

const extensionOf = (name = '') => (name.includes('.') ? name.split('.').pop().toLowerCase() : '')

const iconFor = (name) => {
  const ext = extensionOf(name)
  if (IMAGE_EXTENSIONS.has(ext)) return 'bi-file-earmark-image-fill text-purple-500'
  return FILE_ICONS[ext] || 'bi-file-earmark-fill text-gray-400'
}

const formatSize = (bytes) => {
  if (!bytes) return '-'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = bytes
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return `${n.toFixed(i > 0 && n < 10 ? 1 : 0)} ${units[i]}`
}

/**
 * Per-survey report upload/download panel. Whoever can access the survey can see
 * this list; only staff/superuser (canUpload) can add or remove files - a
 * clientuser gets a read-only, download-only view of the same list.
 */
const SurveyReportsModal = ({ referenceNumber, canUpload, onClose, onChanged }) => {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await reportsAPI.list(referenceNumber)
      setReports(res.data.data || [])
    } catch (err) {
      setError('Failed to load reports.')
    } finally {
      setLoading(false)
    }
  }, [referenceNumber])

  useEffect(() => {
    load()
  }, [load])

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    setUploadError(null)
    setUploading(true)
    try {
      const formData = new FormData()
      files.forEach((f) => formData.append('files', f))
      await reportsAPI.upload(referenceNumber, formData)
      await load()
      onChanged?.()
    } catch (err) {
      const errData = err.response?.data?.errors || err.response?.data?.message
      setUploadError(Array.isArray(errData) ? errData.join(', ') : (errData || 'Upload failed. Please try again.'))
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return
    try {
      await reportsAPI.delete(referenceNumber, id)
      await load()
      onChanged?.()
    } catch (err) {
      alert('Failed to delete file.')
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    if (canUpload) setDragActive(true)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    if (canUpload) doUpload(e.dataTransfer.files)
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <i className="bi bi-folder2-open text-kbl-neon-blue-600"></i>
              Survey Report
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{referenceNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="bi bi-x-lg text-2xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {canUpload && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-kbl-neon-blue-500 bg-kbl-neon-blue-500/10 shadow-glow-neon-blue-soft'
                  : 'border-gray-300 hover:border-kbl-neon-blue-400 hover:bg-gray-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_EXTENSIONS}
                className="hidden"
                onChange={(e) => doUpload(e.target.files)}
              />
              <i className="bi bi-cloud-upload text-3xl text-gray-400"></i>
              <p className="text-sm text-gray-600 mt-2 font-medium">
                {uploading ? 'Uploading...' : 'Drag & drop files here, or click to browse'}
              </p>
              <p className="text-xs text-gray-400 mt-1">Images, PDF, Office docs, ZIP · up to 25MB each · multiple files allowed</p>
            </div>
          )}
          {uploadError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{uploadError}</p>
          )}

          {loading ? (
            <div className="text-center text-gray-500 py-8">Loading...</div>
          ) : error ? (
            <div className="text-center text-red-600 py-8">{error}</div>
          ) : reports.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <i className="bi bi-inbox text-3xl block mb-2 text-gray-300"></i>
              No reports uploaded yet.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
              {reports.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                  <i className={`bi ${iconFor(r.original_filename)} text-2xl flex-shrink-0`}></i>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate" title={r.original_filename}>
                      {r.original_filename}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatSize(r.file_size)} · {r.uploaded_by_username} · {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <a
                    href={r.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 p-1.5 flex-shrink-0"
                    title="Download"
                  >
                    <i className="bi bi-download text-lg"></i>
                  </a>
                  {canUpload && (
                    <button
                      onClick={() => handleDelete(r.id)}
                      className="text-red-600 hover:text-red-800 p-1.5 flex-shrink-0"
                      title="Delete"
                    >
                      <i className="bi bi-trash text-lg"></i>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default SurveyReportsModal
